import React from 'react';
import * as Sentry from "@sentry/react";
import { createPortal } from 'react-dom';
import { Share2, Link as LinkIcon, X } from 'lucide-react';
import { trackPostShare } from '../../services/api';
import { toast } from 'react-toastify';
import { useAuth } from '../../contexts/AuthContext';
import { CommunityError } from "../../errors/sentryErrors";
import { trackEvent } from "../../utils/analytics";

const ShareBottomSheet = ({ post, isOpen, onClose }) => {
  // Hooks must run unconditionally on every render — this used to sit after
  // the `if (!isOpen) return null` below, which is a real Rules-of-Hooks
  // violation (a blocking eslint error, not a warning): toggling `isOpen`
  // would change how many hooks run on that render.
  const { user } = useAuth();

  if (!isOpen) return null;

  const url = `${window.location.origin}/community/${post.slug}`;
  const text = `Check out ${post.user.name}'s trip to ${post.trip.destination} on Romaya!`;

  const handleShare = (channel) => {
    Sentry.addBreadcrumb({
      category: "user-action",
      message: "Shared post",
      level: "info",
    });
    trackEvent("post_shared", { post_id: post.id, channel });
    // Fire tracking silently if logged in
    if (user) {
      trackPostShare(post.id, channel).catch((err) => {
        Sentry.withScope((scope) => {
          scope.setTag("feature", "community");
          scope.setTag("app", "web");
          scope.setTag("error_source", err instanceof TypeError ? "network" : "server");
          Sentry.captureException(new CommunityError(err instanceof Error ? err.message : String(err)));
        });
      });
    }

    if (channel === 'copy_link') {
      navigator.clipboard.writeText(url).catch((err) => {
        Sentry.withScope((scope) => {
          scope.setTag("feature", "community");
          scope.setTag("app", "web");
          scope.setTag("error_source", "browser");
          Sentry.captureException(new CommunityError(err instanceof Error ? err.message : String(err)));
        });
      });
      toast.success('Link copied to clipboard!');
    } else if (channel === 'whatsapp') {
      window.open(`https://wa.me/?text=${encodeURIComponent(text + ' ' + url)}`, '_blank');
    } else if (channel === 'twitter') {
      window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`, '_blank');
    } else if (channel === 'instagram') {
      // Instagram doesn't have a web intent for sharing links easily, fallback to copy
      navigator.clipboard.writeText(url).catch((err) => {
        Sentry.withScope((scope) => {
          scope.setTag("feature", "community");
          scope.setTag("app", "web");
          scope.setTag("error_source", "browser");
          Sentry.captureException(new CommunityError(err instanceof Error ? err.message : String(err)));
        });
      });
      toast.success('Link copied! Open Instagram to share.');
    } else if (channel === 'native') {
      if (navigator.share) {
        navigator.share({
          title: 'Romaya Travel',
          text: text,
          url: url,
        }).catch(err => {
          Sentry.withScope((scope) => {
            scope.setTag("feature", "community");
            scope.setTag("app", "web");
            scope.setTag("error_source", "browser");
            Sentry.captureException(new CommunityError(err instanceof Error ? err.message : String(err)));
          });
        });
      } else {
        navigator.clipboard.writeText(url).catch((err) => {
          Sentry.withScope((scope) => {
            scope.setTag("feature", "community");
            scope.setTag("app", "web");
            scope.setTag("error_source", "browser");
            Sentry.captureException(new CommunityError(err instanceof Error ? err.message : String(err)));
          });
        });
        toast.success('Link copied to clipboard!');
      }
    }
    
    onClose();
  };

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/50 backdrop-blur-sm transition-opacity" onClick={onClose}>
      <div 
        className="bg-white w-full sm:w-96 rounded-t-3xl sm:rounded-3xl p-6 shadow-2xl transform transition-transform animate-slide-up relative"
        onClick={e => { e.stopPropagation(); e.preventDefault(); }}
      >
        <button 
          onClick={e => { e.stopPropagation(); e.preventDefault(); onClose(); }}
          className="absolute top-4 right-4 p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-colors"
        >
          <X size={20} />
        </button>
        <div className="w-12 h-1.5 bg-gray-200 rounded-full mx-auto mb-6 sm:hidden" />
        
        <h3 className="text-xl font-bold text-gray-900 mb-6 text-center">Share this journey</h3>
        
        {/* Post Preview */}
        <div className="flex items-center gap-4 bg-gray-50 p-3 rounded-2xl mb-6">
          <img 
            src={post.coverPhoto?.thumbUrl || post.coverPhoto?.publicUrl || 'https://images.unsplash.com/photo-1476514525535-07fb3b4ae5f1?w=100&q=80'} 
            alt="Preview" 
            className="w-16 h-16 rounded-xl object-cover"
          />
          <div>
            <h4 className="font-bold text-gray-900 line-clamp-1">{post.trip.destination}</h4>
            <p className="text-sm text-gray-500">by {post.user.name}</p>
          </div>
        </div>

        <div className="grid grid-cols-4 gap-4">
          <ShareOption icon={<LinkIcon size={24} />} label="Copy Link" onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleShare('copy_link'); }} color="bg-gray-100 text-gray-700" />
          <ShareOption icon={<svg viewBox="0 0 24 24" fill="currentColor" className="w-6 h-6"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.029 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/></svg>} label="WhatsApp" onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleShare('whatsapp'); }} color="bg-green-100 text-green-600" />
          <ShareOption icon={<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-6 h-6"><rect x="2" y="2" width="20" height="20" rx="5" ry="5"></rect><path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"></path><line x1="17.5" y1="6.5" x2="17.51" y2="6.5"></line></svg>} label="Instagram" onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleShare('instagram'); }} color="bg-pink-100 text-pink-600" />
          <ShareOption icon={<svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>} label="X" onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleShare('twitter'); }} color="bg-blue-100 text-gray-900" />
        </div>
        
        {navigator.share && (
          <button 
            onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleShare('native'); }}
            className="w-full mt-6 py-3 bg-gray-100 hover:bg-gray-200 text-gray-900 font-bold rounded-xl transition-colors flex items-center justify-center gap-2"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5"><rect x="5" y="2" width="14" height="20" rx="2" ry="2"></rect><line x1="12" y1="18" x2="12.01" y2="18"></line></svg>
            More Options
          </button>
        )}
      </div>
    </div>,
    document.body
  );
};

const ShareOption = ({ icon, label, onClick, color }) => (
  <button onClick={onClick} className="flex flex-col items-center gap-2 group">
    <div className={`w-14 h-14 rounded-full flex items-center justify-center transition-transform group-hover:scale-110 active:scale-95 ${color}`}>
      {icon}
    </div>
    <span className="text-xs font-medium text-gray-600">{label}</span>
  </button>
);

export default ShareBottomSheet;
